// ─────────────────────────────────────────────────────────────────────────────
// Price Observations Reducer
// ─────────────────────────────────────────────────────────────────────────────
// State interface, initial state, and reducer for the priceObservations slice.
//
// The review counters (unresolved / to_verify / conflict) are kept in state and
// come from the list metadata: they are what the tab header shows, and there is
// no pushed notification — a count on the tab is what an operator comes back
// to.
// ─────────────────────────────────────────────────────────────────────────────

import { Action, createReducer, on } from '@ngrx/store';
import { IPagination } from 'src/app/shared/models/page.model';
import {
  EMPTY_PRICE_OBSERVATION_FILTERS,
  IPriceImport,
  IPriceObservation,
  IPriceObservationFilters,
  IPriceObservationMetadata,
} from '../models/price-observation.models';
import * as priceObservationsActions from './price-observations.actions';

// ── State interface ──────────────────────────────────────────────────────────

export interface PriceObservationsState {
  observations: IPriceObservation[];
  metadata: IPagination | null;
  imports: IPriceImport[];
  importsMetadata: IPagination | null;
  loading: boolean;
  updating: boolean;
  resolving: boolean;
  keyword: string;
  filters: Partial<IPriceObservationFilters>;
  unresolvedCount: number;
  toVerifyCount: number;
  conflictCount: number;
}

// ── Initial state ────────────────────────────────────────────────────────────

export const initialPriceObservationsState: PriceObservationsState = {
  observations: [],
  metadata: null,
  imports: [],
  importsMetadata: null,
  loading: false,
  updating: false,
  resolving: false,
  keyword: '',
  filters: { ...EMPTY_PRICE_OBSERVATION_FILTERS },
  unresolvedCount: 0,
  toVerifyCount: 0,
  conflictCount: 0,
};

// ── Reducer ──────────────────────────────────────────────────────────────────

const featureReducer = createReducer(
  initialPriceObservationsState,
  on(priceObservationsActions.LoadPriceObservations, (state) => ({
    ...state,
    loading: true,
  })),
  on(priceObservationsActions.LoadPriceObservationsSuccess, (state, action) => {
    // The counters travel with the page, so they stay true after any filter
    // change without a second round trip. `APIResponse` types metadata as
    // plain pagination; this route widens it, and naming that widening once
    // beats three `as any`.
    const metadata = action.response.metadata as IPriceObservationMetadata | null;
    return {
      ...state,
      loading: false,
      observations: action.response.data,
      metadata: action.response.metadata,
      unresolvedCount: metadata?.unresolved ?? state.unresolvedCount,
      toVerifyCount: metadata?.to_verify ?? state.toVerifyCount,
      conflictCount: metadata?.conflict ?? state.conflictCount,
    };
  }),
  on(priceObservationsActions.LoadPriceObservationsError, (state) => ({
    ...state,
    loading: false,
  })),
  on(priceObservationsActions.SetPriceObservationsLoader, (state, action) => ({
    ...state,
    loading: action.isLoading,
  })),

  on(priceObservationsActions.UpdatePriceObservation, (state) => ({
    ...state,
    updating: true,
  })),
  on(priceObservationsActions.UpdatePriceObservationSuccess, (state, action) => ({
    // The row is patched so the screen answers at once; the COUNTERS are not
    // touched. They are server aggregates, and re-deriving them here was a
    // second definition of each bucket: the backend counts an unresolved
    // conflict under `to_verify` too, this reducer counted only `to_verify`,
    // and the header drifted from the list until the operator reloaded. The
    // component reloads on this action, and the fresh metadata is the answer.
    ...state,
    updating: false,
    observations: state.observations.map((row) =>
      row.id === action.observation.id ? action.observation : row),
  })),
  on(priceObservationsActions.UpdatePriceObservationError, (state) => ({
    ...state,
    updating: false,
  })),

  on(priceObservationsActions.KeepPriceObservation, (state) => ({ ...state, resolving: true })),
  on(priceObservationsActions.KeepPriceObservationSuccess, (state) => ({ ...state, resolving: false })),
  on(priceObservationsActions.KeepPriceObservationError, (state) => ({ ...state, resolving: false })),

  on(priceObservationsActions.LoadPriceImportsSuccess, (state, action) => ({
    ...state,
    imports: action.response.data,
    importsMetadata: action.response.metadata,
  })),

  on(priceObservationsActions.SetPriceObservationSearch, (state, action) => ({
    ...state,
    keyword: action.keyword,
  })),
  on(priceObservationsActions.SetPriceObservationFilters, (state, action) => ({
    ...state,
    filters: action.filters,
  })),
);

export function priceObservationsReducer(state: PriceObservationsState | undefined, action: Action) {
  return featureReducer(state, action);
}
